import { useState, useEffect, useCallback } from "react";
const usePaginatedFetch = (apiFunction, { page = 1, limit = 10, search = "", title, ...extraParams }) => {
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState({
    totalPages: 1,
    prevPage: false,
    nextPage: false,
    totalDocs: 0,
    page: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState({
    status: null,
    message: "",
    success: false
  });

  const fetchData = useCallback(
    async (signal = null) => {
      setLoading(true);
      setError(null);

      try {
        const res = await apiFunction({ page, limit, search, ...extraParams }, signal);
        if (signal && signal.aborted) return;


        if (res.success || res.status === 200) {
          const data = res.data;
          setData(Array.isArray(data) ? data : []);
          setMeta({
            totalPages: res.totalPages ?? 1,
            prevPage: Boolean(res.prevPage),
            nextPage: Boolean(res.nextPage),
            totalDocs: res.totalDocs ?? 0,
            page: res.page ?? 1,
          });
          setError({
            status: null,
            message: "",
            success: true
          });
        } else if (res.status === 403) {
          setError({
            status: 403,
            message: res.data?.error || `Failed to fetch ${title}`,
            success: false
          });
        } else {

          setError({
            status: 400,
            message: res.data?.error || `Failed to fetch ${title}`,
            success: false
          });
        }
      } catch (err) {
        if (err.name !== "CanceledError") {
          setError({
            status: 500,
            message: err.message || "Unknown error",
            success: false
          });
        }
      } finally {
        if (!signal || !signal.aborted) setLoading(false);
      }
    },
    [apiFunction, page, limit, search, JSON.stringify(extraParams)]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller.signal);
    return () => controller.abort();
  }, [fetchData]);

  return { data, meta, loading, error, refetch: fetchData };
};

export default usePaginatedFetch;
